import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useCreateCompany } from "@/hooks/mutations/useCreateCompany";
import { useAddCompany } from "@/hooks/sets/useAddCompany";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const createCompanySchema = z.object({
  name: z.string().min(1, {
    message: "Required",
  }),
});

export const CreateCompanyDialog = ({
  onOpenChange,
  onSuccess,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  open: boolean;
}) => {
  const { mutate: createCompany, isPending } = useCreateCompany();
  const addCompany = useAddCompany();

  const form = useForm<z.infer<typeof createCompanySchema>>({
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(createCompanySchema),
  });

  function onSubmit(data: z.infer<typeof createCompanySchema>) {
    mixpanelUtils.trackButtonClick(
      "Create company",
      MIXPANEL_SCREENS.COMPANIES,
    );
    createCompany(
      {
        createCompanyRequest: {
          name: data.name.trim(),
        },
      },
      {
        onSuccess: (newCompany) => {
          form.reset();
          addCompany({
            companyToAdd: {
              ...newCompany,
              companyIntake: null,
              companyInternalConfig: {
                relatedCompanyNames: [],
              },
              stakeholders: [],
              jobs: [],
            },
          });
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        // Reset the form when the dialog is closed.
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Create a new company
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button
                className="rounded-full"
                disabled={isPending}
                type="submit"
              >
                Create company
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
